import { useState, useEffect, useRef, useCallback } from 'react';
import { GameHazard } from '@/lib/types/common/GameMode';
import { getHazardDefinition, FilterAnimationConfig } from '@/lib/config/hazardConfig';
import { getMultipleHazardsStyle } from '@/lib/utils/hazardUtils';
import { ArraySchema } from '@colyseus/schema';

/**
 * Calculates an animated value based on the animation configuration
 */
function calculateAnimatedValue(
    config: FilterAnimationConfig,
    time: number,
    randomOffset: number
): number {
    const { min, max, type, duration = 2000 } = config;
    const range = max - min;

    switch (type) {
        case 'oscillate': {
            // Smooth sine wave oscillation with random offset for variation
            const phase = ((time + randomOffset) / duration) * Math.PI * 2;
            const oscillation = (Math.sin(phase) + 1) / 2; // 0 to 1
            return min + oscillation * range;
        }
        case 'pulse': {
            // Sharp pulse effect
            const phase = ((time + randomOffset) / duration) % 1;
            const pulse = Math.pow(Math.sin(phase * Math.PI), 2);
            return min + pulse * range;
        }
        case 'random': {
            // Random value, updated based on randomInterval
            // The randomOffset is used as seed for consistency within intervals
            return min + randomOffset * range;
        }
        default:
            return (min + max) / 2;
    }
}

/**
 * Builds a CSS filter string from animated values
 */
function buildFilterString(
    staticFilter: string,
    animatedValues: Map<string, { value: number; unit: string }>
): string {
    // Start with static filters, then add/override with animated ones
    const filterParts: Map<string, string> = new Map();

    // Parse static filter
    if (staticFilter) {
        const filterRegex = /([a-zA-Z-]+)\(([^)]+)\)/g;
        let match;
        while ((match = filterRegex.exec(staticFilter)) !== null) {
            filterParts.set(match[1], `${match[1]}(${match[2]})`);
        }
    }

    // Override with animated values
    animatedValues.forEach(({ value, unit }, property) => {
        filterParts.set(property, `${property}(${value.toFixed(3)}${unit})`);
    });

    return Array.from(filterParts.values()).join(' ');
}

export interface UseAnimatedHazardStyleOptions {
    /** Whether the hazards are currently active/shown */
    enabled?: boolean;
    /** Frame rate for animation (default: 60) */
    frameRate?: number;
}

/**
 * Hook that provides animated CSS styles for hazard effects
 * Combines static filters with animated filter properties
 */
export function useAnimatedHazardStyle(
    hazards: ArraySchema<string> | string[] | GameHazard[],
    options: UseAnimatedHazardStyleOptions = {}
): React.CSSProperties {
    const { enabled = true, frameRate = 60 } = options;
    
    const [animatedStyle, setAnimatedStyle] = useState<React.CSSProperties>({});
    const animationFrameRef = useRef<number | null>(null);
    const randomOffsetsRef = useRef<Map<string, number>>(new Map());
    const lastRandomUpdateRef = useRef<Map<string, number>>(new Map());

    // Get all animation configs from active hazards
    const getAnimationConfigs = useCallback((): { 
        configs: FilterAnimationConfig[]; 
        hazardId: string;
        staticFilter: string;
    }[] => {
        const hazardArray = Array.isArray(hazards) ? hazards : Array.from(hazards);
        const result: { configs: FilterAnimationConfig[]; hazardId: string; staticFilter: string }[] = [];

        hazardArray.forEach(hazard => {
            const definition = getHazardDefinition(hazard as GameHazard);
            if (definition?.animatedFilters && definition.animatedFilters.length > 0) {
                result.push({
                    configs: definition.animatedFilters,
                    hazardId: hazard,
                    staticFilter: definition.cssFilter || '',
                });
            }
        });

        return result;
    }, [hazards]);

    // Check if we have any animated filters
    const hasAnimatedFilters = useCallback(() => {
        const hazardArray = Array.isArray(hazards) ? hazards : Array.from(hazards);
        return hazardArray.some(hazard => {
            const definition = getHazardDefinition(hazard as GameHazard);
            return definition?.animatedFilters && definition.animatedFilters.length > 0;
        });
    }, [hazards]);

    useEffect(() => {
        if (!enabled || !hasAnimatedFilters()) {
            // Return static styles when not enabled or no animations
            setAnimatedStyle(getMultipleHazardsStyle(hazards));
            return;
        }

        const frameInterval = 1000 / frameRate;
        let lastFrameTime = 0;

        const animate = (currentTime: number) => {
            // Throttle to target frame rate
            if (currentTime - lastFrameTime < frameInterval) {
                animationFrameRef.current = requestAnimationFrame(animate);
                return;
            }
            lastFrameTime = currentTime;

            const animationData = getAnimationConfigs();
            const allFilters: string[] = [];
            const allTransforms: string[] = [];

            // Process each hazard with animations
            animationData.forEach(({ configs, hazardId, staticFilter }) => {
                const animatedValues = new Map<string, { value: number; unit: string }>();

                configs.forEach(config => {
                    const key = `${hazardId}-${config.property}`;
                    
                    // Handle random offset updates
                    if (config.type === 'random') {
                        const lastUpdate = lastRandomUpdateRef.current.get(key) || 0;
                        const interval = config.randomInterval || 500;
                        
                        if (currentTime - lastUpdate > interval) {
                            randomOffsetsRef.current.set(key, Math.random());
                            lastRandomUpdateRef.current.set(key, currentTime);
                        }
                    } else if (config.type === 'oscillate') {
                        // For oscillate with randomInterval, add periodic random variation
                        if (config.randomInterval) {
                            const lastUpdate = lastRandomUpdateRef.current.get(key) || 0;
                            if (currentTime - lastUpdate > config.randomInterval) {
                                // Add a small random offset to the base oscillation
                                randomOffsetsRef.current.set(
                                    key, 
                                    (randomOffsetsRef.current.get(key) || 0) + (Math.random() - 0.5) * 200
                                );
                                lastRandomUpdateRef.current.set(key, currentTime);
                            }
                        }
                    }

                    // Initialize random offset if not exists
                    if (!randomOffsetsRef.current.has(key)) {
                        randomOffsetsRef.current.set(key, Math.random() * (config.duration || 2000));
                    }

                    const randomOffset = randomOffsetsRef.current.get(key) || 0;
                    const value = calculateAnimatedValue(config, currentTime, randomOffset);
                    
                    animatedValues.set(config.property, {
                        value,
                        unit: config.unit || '',
                    });
                });

                const filterString = buildFilterString(staticFilter, animatedValues);
                if (filterString) {
                    allFilters.push(filterString);
                }
            });

            // Get static styles for hazards without animations
            const staticStyles = getMultipleHazardsStyle(hazards);
            
            // Merge animated filters with static ones
            // The animated filters should take precedence for their specific properties
            const finalFilter = allFilters.length > 0 
                ? mergeFilters(staticStyles.filter as string || '', allFilters.join(' '))
                : staticStyles.filter;

            setAnimatedStyle({
                ...staticStyles,
                filter: finalFilter,
            });

            animationFrameRef.current = requestAnimationFrame(animate);
        };

        animationFrameRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [enabled, hazards, frameRate, getAnimationConfigs, hasAnimatedFilters]);

    if (!enabled) {
        return {};
    }

    return animatedStyle;
}

/**
 * Merges two filter strings, with the second taking precedence for duplicate properties
 */
function mergeFilters(baseFilter: string, overrideFilter: string): string {
    const filterMap = new Map<string, string>();
    const filterRegex = /([a-zA-Z-]+)\(([^)]+)\)/g;

    // Parse base filter
    let match;
    while ((match = filterRegex.exec(baseFilter)) !== null) {
        filterMap.set(match[1], `${match[1]}(${match[2]})`);
    }

    // Override with new values
    filterRegex.lastIndex = 0;
    while ((match = filterRegex.exec(overrideFilter)) !== null) {
        filterMap.set(match[1], `${match[1]}(${match[2]})`);
    }

    return Array.from(filterMap.values()).join(' ');
}

export default useAnimatedHazardStyle;
